import type { CalendarView, ScheduledPost } from '@qlk/shared';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { coQuyen, useDuongDanTrang, useToi } from '../../../lib/auth.js';
import {
  EmptyState,
  HopThoai,
  KhoiChuaCoKenh,
  Nut,
  OChon,
  PageHeader,
  SegmentedControl,
  StatusBadge,
  ToolbarBang,
} from '../../../ui/index.js';
import { useCapNhatPost, useLich, useXoaPost } from '../api.js';
import { ChuThichMau } from '../components/ChuThichMau.js';
import { DialogLenLich } from '../components/DialogLenLich.js';
import { DialogMucTieu } from '../components/DialogMucTieu.js';
import { LuoiThang } from '../components/LuoiThang.js';
import { LuoiTuan } from '../components/LuoiTuan.js';
import { TheBaiDang } from '../components/TheBaiDang.js';
import {
  congNgay,
  congThang,
  khoaNgayVn,
  moTaGio,
  nhanKhoang,
} from '../components/gioThiTruong.js';

export function TrangLichDang() {
  const duongDan = useDuongDanTrang('NỘI DUNG');
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'schedule', 'edit');

  const [view, setView] = useState<CalendarView>('week');
  const [anchor, setAnchor] = useState(khoaNgayVn(new Date()));
  const [channelId, setChannelId] = useState('');
  const [moLenLich, setMoLenLich] = useState(false);
  const [moMucTieu, setMoMucTieu] = useState(false);
  const [dangKeo, setDangKeo] = useState<ScheduledPost | null>(null);
  const [dangChon, setDangChon] = useState<ScheduledPost | null>(null);

  const lich = useLich({ view, anchor, ...(channelId ? { channelId } : {}) });
  const capNhat = useCapNhatPost();
  const xoa = useXoaPost();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const tatCaBai = useMemo(() => lich.data?.days.flatMap((d) => d.posts) ?? [], [lich.data?.days]);

  const kenh = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of tatCaBai) map.set(p.channelId, p.channelName);
    return [...map].map(([value, label]) => ({ value, label }));
  }, [tatCaBai]);

  function doiKhoang(huong: -1 | 1) {
    setAnchor((truoc) => (view === 'week' ? congNgay(truoc, huong * 7) : congThang(truoc, huong)));
  }

  function batDauKeo(e: DragStartEvent) {
    setDangKeo(tatCaBai.find((p) => p.id === e.active.id) ?? null);
  }

  /** Kéo thả chỉ đổi **ngày**, giữ nguyên giờ công khai. */
  function ketThucKeo(e: DragEndEvent) {
    const bai = dangKeo;
    setDangKeo(null);
    if (!bai || !e.over) return;

    const ngayMoi = (e.over.data.current as { date?: string } | undefined)?.date;
    if (!ngayMoi) return;

    const cu = new Date(bai.publishAt);
    const gioVn = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Ho_Chi_Minh',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(cu);

    const moi = new Date(`${ngayMoi}T${gioVn}:00+07:00`).toISOString();
    if (moi === bai.publishAt) return;

    capNhat.mutate({ id: bai.id, publishAt: moi });
  }

  const canhBao = lich.data?.warnings ?? [];

  return (
    <>
      <PageHeader
        breadcrumb={duongDan}
        title="Lịch đăng video"
        phu={lich.data ? nhanKhoang(view, lich.data.from, lich.data.to) : undefined}
        giaiThich="Giờ hiển thị theo GMT+7; giờ vàng của từng thị trường tự quy đổi. Kéo thẻ sang ngày khác để đổi ngày đăng."
        actions={
          <>
            {coSua && (
              <>
                <Nut
                  kieu="phu"
                  onClick={() => {
                    setMoMucTieu(true);
                  }}
                >
                  Tần suất mục tiêu
                </Nut>
                <Nut
                  kieu="chinh"
                  onClick={() => {
                    setMoLenLich(true);
                  }}
                >
                  + Lên lịch
                </Nut>
              </>
            )}
          </>
        }
      />

      <KhoiChuaCoKenh tenTrang="Lịch đăng video" />

      {/*
        Ba thứ này là **điều khiển của lưới lịch**, không phải hành động của
        trang, nên chúng rời `PageHeader` xuống `ToolbarBang`: luật của bộ thành
        phần mới cho đầu trang tối đa một nút primary, mà bản cũ nhồi năm nút
        cộng một ô chọn vào đó.

        "Tuần / Tháng" thành `SegmentedControl` thay cho hai `Nut` tự dựng khung
        — cùng hình với mọi bộ lọc khác trong app.
      */}
      <ToolbarBang
        trai={
          <>
            <div className="flex items-center gap-1">
              <Nut
                kieu="nhe"
                aria-label={view === 'week' ? 'Tuần trước' : 'Tháng trước'}
                className="px-2"
                onClick={() => {
                  doiKhoang(-1);
                }}
              >
                <ChevronLeft size={18} aria-hidden="true" />
              </Nut>
              <span className="so-lieu px-2 text-co-ban font-medium text-chu">
                {lich.data ? nhanKhoang(view, lich.data.from, lich.data.to) : '…'}
              </span>
              <Nut
                kieu="nhe"
                aria-label={view === 'week' ? 'Tuần sau' : 'Tháng sau'}
                className="px-2"
                onClick={() => {
                  doiKhoang(1);
                }}
              >
                <ChevronRight size={18} aria-hidden="true" />
              </Nut>
            </div>

            <SegmentedControl
              aria-label="Cách xem lịch"
              giaTri={view}
              onChange={(v) => {
                setView(v as CalendarView);
              }}
              muc={[
                { giaTri: 'week', nhan: 'Tuần' },
                { giaTri: 'month', nhan: 'Tháng' },
              ]}
            />
          </>
        }
        phai={
          <OChon
            label="Kênh"
            value={channelId}
            onChange={setChannelId}
            options={[{ value: '', label: 'Tất cả kênh' }, ...kenh]}
          />
        }
      />

      <ChuThichMau />

      {canhBao.length > 0 && (
        <div
          role="status"
          className="mb-4 flex items-start gap-3 rounded-the border border-vien bg-warn-nen p-4 text-co-ban text-warn-chu"
        >
          <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">
              {lich.data
                ? `${nhanKhoang(view, lich.data.from, lich.data.to)} chưa đủ chỉ tiêu`
                : 'Chưa đủ chỉ tiêu'}
            </p>
            <ul className="mt-1 list-inside list-disc">
              {canhBao.map((c) => (
                <li key={c.channelId}>
                  {c.channelName}: {c.ready}/{c.target} video
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {lich.isPending && (
        <div className="grid grid-cols-1 gap-4 @min-[700px]/noi-dung:grid-cols-7">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="h-80 animate-pulse rounded-the border border-vien bg-be-mat" />
          ))}
        </div>
      )}

      {lich.isError && (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được lịch đăng"
            description={lich.error.message}
            action={
              <Nut
                onClick={() => {
                  void lich.refetch();
                }}
              >
                Thử lại
              </Nut>
            }
          />
        </div>
      )}

      {lich.data && tatCaBai.length === 0 && (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            icon={CalendarDays}
            title="Chưa có bài đăng nào trong khoảng này"
            description="Lên lịch cho video đã qua bước Kiểm duyệt ở trang Sản xuất nội dung."
            action={
              coSua ? (
                <Nut
                  kieu="chinh"
                  onClick={() => {
                    setMoLenLich(true);
                  }}
                >
                  + Lên lịch
                </Nut>
              ) : undefined
            }
          />
        </div>
      )}

      {lich.data && tatCaBai.length > 0 && (
        <DndContext
          sensors={sensors}
          onDragStart={batDauKeo}
          onDragEnd={ketThucKeo}
          onDragCancel={() => {
            setDangKeo(null);
          }}
        >
          {view === 'week' ? (
            <LuoiTuan lich={lich.data} onMo={setDangChon} />
          ) : (
            <LuoiThang lich={lich.data} onMo={setDangChon} />
          )}

          <DragOverlay>
            {dangKeo && (
              <div className="w-[180px]">
                <TheBaiDang tinh post={dangKeo} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {dangChon && (
        <HopThoai
          title={dangChon.title}
          rong="sm"
          onDong={() => {
            setDangChon(null);
          }}
          dauHop={
            <div className="min-w-0">
              <h2 className="truncate text-tieu-de-hop font-bold text-chu">{dangChon.title}</h2>
              <p className="truncate text-phu text-chu-phu">{dangChon.channelName}</p>
            </div>
          }
          chanHop={
            <>
              {coSua && (
                <Nut
                  kieu="nguy-hiem"
                  dangChay={xoa.isPending}
                  onClick={() => {
                    xoa.mutate(dangChon.id, {
                      onSuccess: () => {
                        setDangChon(null);
                      },
                    });
                  }}
                >
                  <Trash2 size={16} aria-hidden="true" />
                  Bỏ lịch
                </Nut>
              )}
              <Nut
                kieu="phu"
                onClick={() => {
                  setDangChon(null);
                }}
              >
                Đóng
              </Nut>
            </>
          }
        >
          <dl className="space-y-2 text-co-ban">
            <div className="flex gap-2">
              <dt className="w-32 shrink-0 text-chu-mo">Giờ công khai</dt>
              <dd className="so-lieu text-chu">{moTaGio(dangChon.publishAt, dangChon.market)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-32 shrink-0 text-chu-mo">Trạng thái</dt>
              <dd>
                <StatusBadge tone={dangChon.status === 'published' ? 'ok' : 'info'}>
                  {dangChon.status}
                </StatusBadge>
              </dd>
            </div>
            {dangChon.youtubeVideoId && (
              <div className="flex gap-2">
                <dt className="w-32 shrink-0 text-chu-mo">Video YouTube</dt>
                <dd className="text-chu-phu">{dangChon.youtubeVideoId}</dd>
              </div>
            )}
            {dangChon.lastError && (
              <div className="flex gap-2">
                <dt className="w-32 shrink-0 text-chu-mo">Lỗi gần nhất</dt>
                <dd className="text-bad-chu">{dangChon.lastError}</dd>
              </div>
            )}
          </dl>
        </HopThoai>
      )}

      {moLenLich && (
        <DialogLenLich
          onDong={() => {
            setMoLenLich(false);
          }}
        />
      )}

      {moMucTieu && (
        <DialogMucTieu
          kenh={kenh}
          onDong={() => {
            setMoMucTieu(false);
          }}
        />
      )}
    </>
  );
}
